import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Clock, Eye, Hand, Shuffle, Square, UserPlus } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Badge, Button } from '../../components/ui';
import { STAT_CARD_BASE } from '../../components/ui/StatCard';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import MicroSimGroupResults from '../../components/microsim/MicroSimGroupResults';
import ManualPairingDialog from '../../components/microsim/ManualPairingDialog';
import useWebSocket from '../../hooks/useWebSocket';
import { useMicroSimPaths } from '../../utils/microSimPaths';
import { withNumber } from '../../utils/participantNumber';
import microSimService from '../../services/microSimService';
import { showError, showSuccess } from '../../utils/toast';

/**
 * Trainer console for a running micro simulation: every pair's progress, who is
 * waiting on whom, idle pairs (D11: no turn timer — the trainer nudges instead),
 * late joiners, and End.
 *
 * Live updates arrive on `msim:progress`, sent only to a room the server lets this
 * trainer join after checking they manage the launch. A 15-second refresh backs it up.
 * Skill scores appear per pair only once that pair has finished.
 *
 * The Results tab (Phase 2) shows the group heatmap, radar and choices; it re-fetches
 * whenever the number of finished pairs changes, so it fills in live as pairs complete.
 */

const IDLE_WARN_MS = 2 * 60 * 1000;
const REFRESH_MS = 15000;

const STATUS = {
  in_progress: { label: 'Playing', variant: 'primary' },
  round_end: { label: 'Between rounds', variant: 'warning' },
  completed: { label: 'Finished', variant: 'success' },
  abandoned: { label: 'Ended', variant: 'default' },
};

// Pairs a waiting person can still join as an observer.
const RUNNING = ['in_progress', 'round_end'];

const named = (p) => withNumber(p.name, p.number);
const pairName = (run) => run.players.map(named).join(' & ');

const idleFor = (iso) => (iso ? Date.now() - new Date(iso).getTime() : 0);
const formatIdle = (ms) => {
  const m = Math.floor(ms / 60000);
  return m < 1 ? 'just now' : `${m} min ago`;
};

const MicroSimLaunchConsolePage = () => {
  const { launchId } = useParams();
  const navigate = useNavigate();
  const paths = useMicroSimPaths();
  const homePath = paths.home;
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [tab, setTab] = useState(null); // 'pairs' | 'results'; null = pick by status
  const [, setTick] = useState(0);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [choosing, setChoosing] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await microSimService.getLaunch(launchId));
    } catch (err) {
      showError(err.userMessage || 'Could not load this micro simulation');
      navigate(homePath);
    }
  }, [launchId, navigate, homePath]);

  useEffect(() => { load(); }, [load]);

  const sessionId = data?.launch?.sessionId || null;
  const { on, off, emit, isConnected } = useWebSocket(sessionId);

  useEffect(() => {
    if (isConnected) emit('msim:watch-launch', { launchId });
  }, [isConnected, launchId, emit]);

  useEffect(() => {
    const onProgress = (row) => {
      setData((d) => (d ? { ...d, runs: d.runs.map((r) => (r.runId === row.runId ? row : r)) } : d));
    };
    const onEnded = () => load();
    on('msim:progress', onProgress);
    on('msim:ended', onEnded);
    return () => { off('msim:progress', onProgress); off('msim:ended', onEnded); };
  }, [on, off, load]);

  // Backup refresh (picks up new joiners too), and a tick to age the idle times.
  useEffect(() => {
    const refresh = setInterval(load, REFRESH_MS);
    const tick = setInterval(() => setTick((t) => t + 1), 15000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [load]);

  // Resolves true when it worked, so a dialog can close only on success.
  const act = async (key, fn, success) => {
    setBusy(key);
    try {
      const res = await fn();
      setData(res);
      if (success) showSuccess(success(res));
      return true;
    } catch (err) {
      showError(err.userMessage || err.message || 'That did not work');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const pairChosen = async (spec) => {
    const ok = await act('manual', () => microSimService.pairManually(launchId, spec), (res) => `${res.paired} placed`);
    if (ok) setChoosing(false);
    else load(); // someone may have left; show the room as it is now
  };

  if (!data) {
    return (
      <DashboardLayout title="Micro simulation">
        <div className="mx-auto max-w-5xl px-4 py-6"><div className={`${STAT_CARD_BASE} h-48 animate-pulse`} /></div>
      </DashboardLayout>
    );
  }

  const { launch, runs, unpaired, canReshuffle } = data;
  const ended = launch.status !== 'active';
  const finished = runs.filter((r) => r.status === 'completed').length;
  const activeTab = tab || (ended ? 'results' : 'pairs');
  // Pairs that would be cut short by ending now — the confirm says how many.
  const stillPlaying = runs.filter((r) => !['completed', 'abandoned'].includes(r.status)).length;

  return (
    <DashboardLayout title="Micro simulation">
      <div className="mx-auto w-full max-w-5xl space-y-5 px-4 py-6 lg:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(paths.session(launch.sessionId))}
            className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-800 dark:text-neutral-400"
          >
            <ArrowLeft size={15} /> Back to session
          </button>
          {!isConnected && <Badge variant="warning" size="sm">Offline — refreshing every 15s</Badge>}
        </div>

        <div className={`${STAT_CARD_BASE} p-5`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Badge variant={ended ? 'default' : 'live'} size="sm" dot={!ended}>{ended ? 'Ended' : 'Running'}</Badge>
                <span className="text-xs text-neutral-500">Paired micro simulation</span>
              </div>
              <h2 className="mt-2 text-lg font-semibold text-neutral-900 dark:text-neutral-100">{launch.title}</h2>
              <p className="mt-0.5 text-sm text-neutral-500">
                {finished} of {runs.length} {runs.length === 1 ? 'pair' : 'pairs'} finished
              </p>
            </div>
            {!ended && (
              <div className="flex flex-wrap gap-2">
                {canReshuffle && (
                  <Button variant="secondary" size="sm" loading={busy === 'reshuffle'} disabled={Boolean(busy)}
                    onClick={() => act('reshuffle', () => microSimService.reshuffle(launchId), () => 'Pairs reshuffled')}>
                    <Shuffle size={14} className="mr-1.5" /> Reshuffle pairs
                  </Button>
                )}
                <Button variant="danger" size="sm" loading={busy === 'end'} disabled={Boolean(busy)}
                  onClick={() => setConfirmEnd(true)}>
                  <Square size={13} className="mr-1.5" /> End for everyone
                </Button>
              </div>
            )}
          </div>

          {!ended && unpaired.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-amber-50 px-4 py-3 ring-1 ring-inset ring-amber-200">
              <p className="text-sm text-amber-900">
                <span className="font-semibold">{unpaired.length} not paired yet:</span> {unpaired.map(named).join(', ')}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" size="sm" disabled={Boolean(busy)} onClick={() => setChoosing(true)}>
                  <Hand size={14} className="mr-1.5" /> Choose pairs
                </Button>
                <Button variant="secondary" size="sm" loading={busy === 'late'} disabled={Boolean(busy)}
                  onClick={() => act('late', () => microSimService.pairLateJoiners(launchId), (res) => `${res.paired} paired`)}>
                  <UserPlus size={14} className="mr-1.5" /> Pair at random
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="inline-flex rounded-xl bg-neutral-100 p-1 dark:bg-neutral-800" role="tablist">
          {[['pairs', `Pairs (${runs.length})`], ['results', `Results (${finished} finished)`]].map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={activeTab === key}
              onClick={() => setTab(key)}
              className={`rounded-lg px-3.5 py-1.5 text-sm font-semibold ${activeTab === key ? 'bg-white text-neutral-900 shadow-sm dark:bg-neutral-900 dark:text-neutral-100' : 'text-neutral-500 hover:text-neutral-800'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {activeTab === 'results' && <MicroSimGroupResults launchId={launchId} refreshKey={finished} />}

        {activeTab === 'pairs' && (
        <div className="grid gap-3 md:grid-cols-2">
          {runs.map((run) => {
            const status = STATUS[run.status] || STATUS.in_progress;
            const idleMs = idleFor(run.lastActivityAt);
            const idle = run.status === 'in_progress' && idleMs > IDLE_WARN_MS;
            return (
              <div key={run.runId} className={`${STAT_CARD_BASE} p-4 ${idle ? 'ring-2 ring-amber-300' : ''}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                    {pairName(run)}
                  </p>
                  <Badge variant={status.variant} size="sm">{status.label}</Badge>
                </div>
                {run.observers.length > 0 && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-neutral-500">
                    <Eye size={12} /> Observed by {run.observers.map(named).join(', ')}
                  </p>
                )}

                <div className="mt-3 flex gap-0.5" aria-hidden="true">
                  {Array.from({ length: run.turnsTotal }, (_, i) => (
                    <div key={i} className={`h-1.5 flex-1 rounded-full ${i < run.turnsPlayed ? 'bg-primary-500' : 'bg-neutral-200 dark:bg-neutral-800'}`} />
                  ))}
                </div>
                <p className="mt-2 text-xs text-neutral-600 dark:text-neutral-400">
                  {run.status === 'in_progress' && `Round ${run.round + 1} · Turn ${run.step + 1} of ${run.stepCount} — waiting on ${run.awaitingName}`}
                  {run.status === 'round_end' && `Round ${run.round + 1} done — reading the outcome`}
                  {run.status === 'completed' && 'Both rounds complete'}
                  {run.status === 'abandoned' && 'Stopped before finishing'}
                </p>
                {run.status === 'in_progress' && (
                  <p className={`mt-1 flex items-center gap-1 text-xs ${idle ? 'font-semibold text-amber-700' : 'text-neutral-400'}`}>
                    <Clock size={12} /> Last move {formatIdle(idleMs)}{idle ? ' — may need a nudge' : ''}
                  </p>
                )}
                {run.outcomes.length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    {run.outcomes.map((o) => (
                      <li key={o.round} className="flex items-center gap-1.5 text-xs text-neutral-600 dark:text-neutral-400">
                        <CheckCircle2 size={12} className="text-emerald-500" /> R{o.round + 1}: {o.title}
                      </li>
                    ))}
                  </ul>
                )}
                {run.results && (
                  <div className="mt-3 grid grid-cols-2 gap-2 border-t border-neutral-100 pt-3 dark:border-neutral-800">
                    {run.players.map((p) => {
                      const r = run.results[p.slot];
                      const label = (key) => r.skills.find((s) => s.key === key)?.label;
                      return (
                        <div key={p.slot} className="text-xs">
                          <p className="font-semibold text-neutral-800 dark:text-neutral-200">{named(p)}</p>
                          <p className="text-emerald-700">↑ {label(r.strongest) || '—'}</p>
                          <p className="text-amber-700">↻ {label(r.practiceFocus) || 'Nothing to focus on'}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmEnd}
        onClose={() => setConfirmEnd(false)}
        onConfirm={() => act('end', () => microSimService.endLaunch(launchId), () => 'Micro simulation ended')}
        title="End the micro simulation?"
        message={stillPlaying > 0
          ? `${stillPlaying} ${stillPlaying === 1 ? 'pair is' : 'pairs are'} still playing and will stop without results. Pairs that have finished keep theirs.`
          : 'Every pair has finished. This closes the micro simulation for everyone; the results stay available.'}
        confirmText="End for everyone"
        variant="danger"
      />

      <ManualPairingDialog
        isOpen={choosing && !ended}
        onClose={() => setChoosing(false)}
        people={unpaired}
        runningPairs={runs.filter((r) => RUNNING.includes(r.status)).map((r) => ({ runId: r.runId, label: pairName(r) }))}
        onSubmit={pairChosen}
        submitting={busy === 'manual'}
        submitLabel="Make these pairs"
        title="Choose pairs for people waiting"
      />
    </DashboardLayout>
  );
};

export default MicroSimLaunchConsolePage;
